import { DvToolbarAction } from '@devinco/ui/grid';
import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { FilterModelWithKeys } from '@features/vt-grid/interfaces/grid-context';
import { GridOptions } from '@features/vt-grid/interfaces/grid-options';
import { Entity } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { TranslateService } from '@ngx-translate/core';
import { ExpenseEditDialogComponent } from '@pages/trips-page/components/trip-expenses-tab/expense-edit-dialog/expense-edit-dialog.component';
import { ExpensesGridService } from './expenses-grid.service';
import {
  GridApi,
  GridReadyEvent,
  IRowNode,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { BehaviorSubject, filter, of, switchMap, take, tap } from 'rxjs';
import { TripDto } from '@pages/trips-page/components/trips-grid/interfaces';
import { TripExpenseDto } from '@pages/trips-page/components/trip-expenses-tab/interfaces';
import { DialogService } from '@core/services/dialog.service';
import { DvDialogComponent } from '@devinco/ui/dds';

@Component({
  selector: 'app-expenses-grid',
  templateUrl: './expenses-grid.component.html',
  styleUrls: ['./expenses-grid.component.scss'],
  providers: [ExpensesGridService, GridOptionsService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ExpensesGridComponent implements OnInit {
  private gridService = inject(ExpensesGridService);
  private gridOptionsService = inject(GridOptionsService);
  private dialog = inject(DialogService);
  private api = inject(RestApiService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private translate = inject(TranslateService);

  @Input() entityChanged$: BehaviorSubject<string>;
  @Input() tripDistance?: number;

  gridOptions: GridOptions;
  actions = signal<DvToolbarAction[]>([]);

  private selectedNode: IRowNode | undefined;
  private canEditPage: boolean;
  private gridApi: GridApi | undefined;
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.permissionsService
      .hasPermissions([RolesAndPermissions.TripEdit])
      .pipe(take(1))
      .subscribe(hasPermissions => {
        this.canEditPage = hasPermissions;
        this.setupVariables();
        this.setupEvents();
      });
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
  }

  onSelectionChanged({ api }: SelectionChangedEvent): void {
    this.selectedNode = api.getSelectedNodes()[0] as IRowNode | undefined;
    this.toggleGridToolbarItems();
  }

  private toggleGridToolbarItems(): void {
    this.actions.update(actions =>
      actions.map(action => {
        if (action.icon === 'delete') {
          return {
            ...action,
            disabled: !this.selectedNode || !this.canEditPage,
          };
        }
        if (action.icon === 'edit') {
          return {
            ...action,
            disabled: !this.selectedNode || !this.canEditPage,
          };
        }
        if (action.icon === 'add_box') {
          return { ...action, disabled: !this.canEditPage };
        }
        return action;
      })
    );
  }

  private addEditExpense = (isAdding?: boolean): void => {
    const currentEntityId = this.entityChanged$.getValue();
    this.api
      .getInfo<Pick<TripDto, 'Distance' | 'TripType'>>(
        Entity.Trip,
        currentEntityId,
        ['Distance', 'TripType']
      )
      .pipe(
        switchMap(resp => {
          return this.openEditExpenseDialog(
            !!isAdding,
            currentEntityId,
            resp
          ).closed.pipe(
            switchMap(({ source, responseData }) => {
              if (source !== 'confirm') return of();

              return this.api.getInfo<TripExpenseDto>(
                Entity.TripExpense,
                String(responseData.TripExpenseID)!,
                this.gridService.getColumnDefs.map(colDef => colDef.field!)
              );
            }),
            tap((result: TripExpenseDto) => {
              if (!isAdding) {
                this.gridApi?.applyServerSideTransaction({
                  update: [{ ...this.selectedNode?.data, ...result }],
                });
              } else {
                this.gridApi?.applyServerSideTransaction({
                  add: [result],
                });
              }
            })
          );
        }),
        take(1)
      )
      .subscribe();
  };

  private setupEvents(): void {
    this.entityChanged$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(entityId => !!entityId)
      )
      .subscribe(entityId => {
        this.gridOptions.context.extraFilter = this.getFilterModel(entityId);
        this.gridApi?.onFilterChanged();
      });
  }

  private setupVariables(): void {
    this.actions.set(
      this.gridService.getToolbarActions(
        this.addEditExpense,
        this.addEditExpense
      )
    );
    this.gridOptions = this.gridOptionsService.serverSide(
      this.gridService.getColumnDefs,
      Entity.TripExpense
    );
    this.toggleGridToolbarItems();
  }

  private getFilterModel(filterId: string): FilterModelWithKeys {
    return {
      TripID: {
        filterType: 'number',
        type: 'equals',
        filter: filterId,
      },
    };
  }

  private openEditExpenseDialog(
    isAdding: boolean,
    currentEntityId: string,
    tripInfo: Pick<TripDto, 'Distance' | 'TripType'>
  ): DvDialogComponent<Partial<TripExpenseDto>, ExpenseEditDialogComponent> {
    const selectedNodeData = this.selectedNode?.data;
    return this.dialog.open({
      size: 'md',
      title: isAdding
        ? 'Trip.ExpenseSetup.AddDialog.Title'
        : 'Trip.ExpenseSetup.EditDialog.Title',
      confirmText: isAdding ? 'General.Add' : 'General.Save',
      cancelText: 'General.Cancel',
      contentComponent: ExpenseEditDialogComponent,
      hideFooter: true,
      data: isAdding
        ? {
            TripID: currentEntityId,
            Distance: tripInfo.Distance,
            TripExpenseID: 0,
            TripType: tripInfo.TripType,
          }
        : {
            ...selectedNodeData,
            TripID: currentEntityId,
            ExpenseType: selectedNodeData?.TranslationKey
              ? this.translate.instant(selectedNodeData?.TranslationKey)
              : selectedNodeData.Description,
            Distance: tripInfo.Distance,
            TripExpenseID: selectedNodeData?.TripExpenseID,
            TripType: tripInfo.TripType,
          },
    });
  }
}
